import type { Answers, TSuppressionProfile } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";

/**
 * quiz-before-trt deriver. Wraps `deriveBoostProfile` (the cortisol-profile
 * pattern) with three additions:
 *   1. Passthrough: `age`, `energy-3pm`, `body-composition`, `when-started`,
 *      `results-want` reuse quiz-v6 ids/values verbatim, so no translation.
 *      The considering-TRT questions have no ignite-namespace equivalent, so
 *      nothing is manufactured into the base deriver's input.
 *   2. Symptom severity: `trt-status`, `libido`, `brain-fog` feed severity via
 *      the `extraSignals` seam (worst -> tier1, second-worst -> tier2;
 *      `trt-status=researching` -> tier3). `trt-hesitation` is persuasion
 *      only -- never scored.
 *   3. Forced primary: `blocked-testosterone` leads the verdict on every path
 *      via the `forcePrimaryCause` seam -- the quiz's entire promise is
 *      "blocked, not broken." The organic top scorer stays visible as
 *      Secondary (AROMATASE LOAD for soft/overweight builds, so the 2x2 grid
 *      names two of the three thieves).
 *
 * Pure function of `answers` -- reproducible server-side from the persisted
 * submission (rule 11).
 */

/** Ids whose values are ignite-verbatim -- copied through untranslated. */
const PASSTHROUGH_IDS = [
  "age",
  "energy-3pm",
  "body-composition",
  "when-started",
  "results-want",
] as const;

/**
 * Symptom-intensity severity mapping. Worst answer -> tier1, second-worst ->
 * tier2 (the cortisol/quiz-v7 cohort convention); `trt-status=researching` is
 * a weak demographic signal -> tier3.
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  "trt-status": ["told-low"],
  libido: ["significantly"],
  "brain-fog": ["daily"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  "trt-status": ["considering", "symptomatic"],
  libido: ["somewhat"],
  "brain-fog": ["most-days"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  "trt-status": ["researching"],
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

/** Passthrough ignite-namespace answers only -- translation, not manufacture. */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  return out;
}

export function deriveQuizBeforeTrtProfile(answers: Answers): TSuppressionProfile {
  return deriveBoostProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "blocked-testosterone",
  });
}
